"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Globe, ShieldCheck, CircleCheck as CheckCircle2, Building2, ChevronRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
export default function HeroPartnerships() {
  const shouldReduceMotion = useReducedMotion();
  const keyHighlights = [{
    title: "Direct OEM Partnerships",
    description: "Direct ties with certified global instrument manufacturers.",
    icon: Globe
  }, {
    title: "Compliance & Qualification",
    description: "Full lifecycle support across Ireland and UK regulated labs.",
    icon: ShieldCheck
  }, {
    title: "Local Application Engineers",
    description: "On-site installation, method setup, and ongoing maintenance.",
    icon: Building2
  }];
  const statItems = [{
    value: "25+",
    label: "Global Brand Partners"
  }, {
    value: "Focus",
    label: "Regulated Compliance"
  }, {
    value: "24h",
    label: "Local Field Response"
  }];
  return <section id="heroglobalpartnerships" data-nav="dark" className="relative overflow-hidden bg-sidebar text-sidebar-foreground py-20 lg:py-28">
      {/* Decorative ambient background accents */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden opacity-30">
        <div className="absolute -top-40 -right-40 size-[500px] rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute bottom-0 -left-20 size-[420px] rounded-full bg-sidebar-accent blur-3xl" />
      </div>

      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left Column: Heading, Copy, Actions */}
          <div className="lg:col-span-7 flex flex-col items-start">
            {/* Breadcrumb */}
            <motion.nav aria-label="Breadcrumb" initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.45
          }} className="mb-4">
              <ol className="flex items-center gap-2 text-sm text-sidebar-foreground/75">
                <li>
                  <Link to="/HomePage" className="hover:text-white transition-colors">
                    Home
                  </Link>
                </li>
                <li aria-hidden="true" className="text-sidebar-foreground/50">
                  <ChevronRight className="size-3.5" />
                </li>
                <li>
                  <span aria-current="page" className="font-medium text-white">
                    Suppliers
                  </span>
                </li>
              </ol>
            </motion.nav>

            <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.05
          }}>
              <Badge variant="outline" className="mb-6 gap-2 rounded-full border-sidebar-border bg-sidebar-accent/50 px-3.5 py-1 text-xs font-semibold text-sidebar-foreground">
                <Globe className="size-3.5 text-primary" />
                <span>Global Instrumentation Network</span>
              </Badge>
            </motion.div>

            <motion.h1 initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.1
          }} className="font-default text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.12]">
              Global Technology Partnerships Engineered for Local Application
            </motion.h1>

            <motion.p initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.2
          }} className="mt-6 text-base sm:text-lg text-sidebar-foreground/80 max-w-2xl leading-relaxed">
              We connect regulated pharmaceutical, biopharma, and testing laboratories across Ireland and the UK with worldwide analytical technology leaders, backed by dedicated local lifecycle qualification and application support.
            </motion.p>

            {/* Dual CTAs */}
            <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.3
          }} className="mt-8 flex flex-wrap items-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="w-full sm:w-auto rounded-md bg-primary text-primary-foreground font-semibold shadow-md hover:bg-primary/90 transition-colors">
                <Link to="#partners" className="inline-flex items-center justify-center gap-2">
                  <span>Explore Our Partners</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto rounded-md border-sidebar-border bg-sidebar-accent/40 text-sidebar-foreground font-semibold hover:bg-sidebar-accent hover:text-white transition-colors">
                <Link to="#contact">
                  <span>Discuss Your Needs</span>
                </Link>
              </Button>
            </motion.div>

            {/* Stats bar */}
            <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.4
          }} className="mt-12 pt-8 border-t border-sidebar-border/60 grid grid-cols-3 gap-6 w-full max-w-xl">
              {statItems.map((stat, i) => <div key={i} data-index={i} className="flex flex-col">
                  <span className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                    {stat.value}
                  </span>
                  <span className="text-xs sm:text-sm text-sidebar-foreground/75 mt-1">
                    {stat.label}
                  </span>
                </div>)}
            </motion.div>
          </div>

          {/* Right Column: Visual Showcase Card */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.25
        }} className="lg:col-span-5 flex flex-col gap-4">
            <div className="relative rounded-2xl border border-sidebar-border bg-sidebar-accent/30 p-2 shadow-xl backdrop-blur-xs">
              <div className="relative aspect-4/3 w-full overflow-hidden rounded-xl bg-sidebar">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/laboratory_analytical_instrumentation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/laboratory_analytical_instrumentation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/laboratory_analytical_instrumentation.png-wvc-sizes" alt="Precision analytical testing equipment in a regulated laboratory" loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />
                <div className="absolute inset-0 bg-gradient-to-t from-sidebar via-sidebar/20 to-transparent" />

                {/* Floating badge inside image */}
                <div className="absolute bottom-4 left-4 right-4 rounded-lg border border-sidebar-border bg-sidebar/90 p-3.5 backdrop-blur-md">
                  <div className="flex items-center gap-3">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground font-bold">
                      <CheckCircle2 className="size-5" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                        Certified Validation
                      </p>
                      <p className="text-sm font-medium text-white">
                        Full IQ/OQ/PQ Compliance Delivery
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick feature callouts */}
            <div className="grid grid-cols-1 gap-3">
              {keyHighlights.map((item, i) => {
              const IconComponent = item.icon;
              return <div key={i} data-index={i} className="flex items-start gap-3.5 rounded-xl border border-sidebar-border bg-sidebar-accent/20 p-3.5 transition-[background-color,border-color] hover:bg-sidebar-accent/40">
                    <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-sidebar-accent text-primary">
                      <IconComponent className="size-4" />
                    </div>
                    <div className="flex flex-col">
                      <h2 className="text-sm font-semibold text-white">
                        {item.title}
                      </h2>
                      <p className="text-xs text-sidebar-foreground/75 leading-relaxed mt-0.5">
                        {item.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}